import React, { useRef, useState } from "react";
import { Upload, FileDown, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { autoMapColumns, buildRecords, downloadTemplate, mergeRecords, parseSpreadsheet } from "../lib/importUtils";

const MODES = [
  { value: "append", label: "Add new records only", hint: "Rows whose ID already exists are skipped." },
  { value: "merge", label: "Add new and update existing", hint: "Rows whose ID already exists are updated in place." },
  { value: "replace", label: "Replace all records", hint: "Deletes every current record first." },
];

export default function ImportDialog({ open, onOpenChange, title, fields, existingRows, idPrefix, onImport, sheetName }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("append");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setParsed(null);
    setMapping({});
    setMode("append");
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFile = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setBusy(true);
    setError("");
    try {
      const result = await parseSpreadsheet(picked);
      setFile(picked);
      setParsed(result);
      setMapping(autoMapColumns(result.headers, fields));
    } catch (err) {
      setFile(null);
      setParsed(null);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const preview = parsed ? buildRecords(parsed.rows.slice(0, 5), fields, mapping, idPrefix).records : [];
  const mappedCount = Object.values(mapping).filter(Boolean).length;
  const previewColumns = fields.filter((f) => mapping[f.key]).slice(0, 6);

  const runImport = () => {
    if (!parsed) return;
    const { records, skipped, generatedIds } = buildRecords(parsed.rows, fields, mapping, idPrefix);
    if (records.length === 0) {
      setError("No usable rows found. Check the column mapping below.");
      return;
    }
    if (mode === "replace" && !window.confirm(`Replace all ${existingRows.length} existing records with ${records.length} imported rows?`)) return;

    const merged = mergeRecords(existingRows, records, mode);
    onImport(merged.rows);

    const parts = [];
    if (merged.added) parts.push(`${merged.added} added`);
    if (merged.updated) parts.push(`${merged.updated} updated`);
    if (merged.skipped) parts.push(`${merged.skipped} duplicate${merged.skipped === 1 ? "" : "s"} skipped`);
    if (skipped) parts.push(`${skipped} empty row${skipped === 1 ? "" : "s"} ignored`);
    if (generatedIds) parts.push(`${generatedIds} ID${generatedIds === 1 ? "" : "s"} generated`);
    toast.success(`Import complete — ${parts.join(", ")}`);
    close(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Import {title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
          {/* Step 1 — file */}
          <div className="rounded-md border border-slate-200 p-3.5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-sm text-[#082B4D]">1. Choose a CSV or Excel file</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  The first row must contain column headings. Download the template for the exact format.
                </div>
              </div>
              <Button
                data-testid="import-template-btn"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => downloadTemplate(fields, `${idPrefix.toLowerCase()}-import-template.xlsx`, sheetName || title)}
              >
                <FileDown className="w-3.5 h-3.5" /> Template
              </Button>
            </div>
            <input
              data-testid="import-file-input"
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFile}
              className="mt-3 block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-[#183C55] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-[#0E2336]"
            />
            {busy && <div className="text-[11px] text-slate-500 mt-2">Reading file…</div>}
            {file && parsed && (
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 mt-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {file.name} — {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"}, {parsed.headers.length} columns detected
              </div>
            )}
            {error && (
              <div className="flex items-start gap-1.5 text-[11px] text-rose-700 mt-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {error}
              </div>
            )}
          </div>

          {parsed && (
            <>
              {/* Step 2 — mapping */}
              <div className="rounded-md border border-slate-200 p-3.5">
                <div className="font-semibold text-sm text-[#082B4D]">2. Match columns</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {mappedCount} of {fields.length} fields matched automatically. Adjust anything that looks wrong.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mt-3">
                  {fields.map((f) => (
                    <div key={f.key} className="flex items-center gap-2">
                      <label className="text-[11px] text-slate-600 w-32 shrink-0 truncate" title={f.label}>{f.label}</label>
                      <select
                        data-testid={`import-map-${f.key}`}
                        value={mapping[f.key] || ""}
                        onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                        className="flex-1 min-w-0 border border-slate-200 rounded-md px-2 py-1 text-xs bg-white"
                      >
                        <option value="">— skip —</option>
                        {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 3 — mode */}
              <div className="rounded-md border border-slate-200 p-3.5">
                <div className="font-semibold text-sm text-[#082B4D]">3. How should these rows be applied?</div>
                <div className="space-y-2 mt-2.5">
                  {MODES.map((m) => (
                    <label key={m.value} className="flex items-start gap-2 cursor-pointer">
                      <input
                        data-testid={`import-mode-${m.value}`}
                        type="radio"
                        name="import-mode"
                        checked={mode === m.value}
                        onChange={() => setMode(m.value)}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="text-xs font-medium text-slate-700">{m.label}</span>
                        <span className="block text-[11px] text-slate-500">{m.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {mode === "replace" && (
                  <div className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 mt-3">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                    All {existingRows.length} current records will be deleted and replaced.
                  </div>
                )}
              </div>

              {/* Preview */}
              {preview.length > 0 && (
                <div className="rounded-md border border-slate-200 overflow-hidden">
                  <div className="px-3.5 py-2 text-[11px] font-semibold text-slate-600 bg-slate-50 border-b border-slate-200">
                    Preview — first {preview.length} row{preview.length === 1 ? "" : "s"}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="bg-white">
                          {previewColumns.map((c) => (
                            <th key={c.key} className="text-left py-1.5 px-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((r, i) => (
                          <tr key={r.id || i} className={i % 2 ? "bg-slate-50/60" : ""}>
                            {previewColumns.map((c) => (
                              <td key={c.key} className="py-1.5 px-2.5 text-slate-700 whitespace-nowrap max-w-[160px] truncate">{String(r[c.key] ?? "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          <Button
            data-testid="import-confirm-btn"
            disabled={!parsed || busy}
            onClick={runImport}
            style={{ background: "#183C55", color: "white" }}
            className="gap-1.5"
          >
            <Upload className="w-3.5 h-3.5" /> Import {parsed ? `${parsed.rows.length} rows` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
